import { useEffect, useRef, useState, type RefObject } from 'react';
import { Mic, Settings2 } from 'lucide-react';
import { cn } from '@vd/ui';
import { MicDevicePickerModal } from './MicDevicePickerModal';
import styles from './MicDictation.module.css';

/**
 * Voice-to-text dictation button for the ticket description.
 *
 * Wraps the browser-native Web Speech API (SpeechRecognition). Clicking
 * the button starts continuous recognition; final transcripts are
 * inserted at the textarea's current cursor position via the splice
 * pattern. Interim transcripts are ignored — they'd cause churn that
 * fights the user's keystrokes.
 *
 * **Why Web Speech and not Whisper / Deepgram**: streaming latency.
 * Chrome ships transcripts in 200-500ms during speech; Whisper is
 * batch-oriented with 1-3s per chunk. Accuracy is "good enough" for
 * bug-report dictation; the user can correct typos. For Toado v2's
 * voice-narrated multi-ticket recording, a different code path with
 * cloud transcription will live separately.
 *
 * **Browser support**: only Chrome / Edge. Firefox has no Web Speech
 * API; the component renders nothing there. That matches the product's
 * stated non-goal (PRD: no Firefox/Safari support).
 *
 * **Privacy note**: Chrome's recognizer streams audio to Google's
 * servers under the hood. The tooltip surfaces this implicitly via the
 * "your browser's speech recognition" phrasing.
 *
 * Design constraints baked in (from codex review of the plan):
 *   1. Iterate from `event.resultIndex`, not 0 — Web Speech delivers
 *      the FULL results list each event; iterating from 0 would
 *      re-insert previously-committed finals on every subsequent event.
 *   2. Read description via `valueRef`, not the closure-captured prop
 *      — multiple onresult events arriving in quick succession would
 *      each splice into stale `value` and overwrite each other.
 *   3. Set `listeningRef.current = false` BEFORE stop/state changes —
 *      otherwise the `onend` it triggers would see `true` and restart.
 *      Especially important on permission denial (which fires `onend`).
 *   4. 500ms restart cooldown — Chrome throws InvalidStateError on
 *      tight onend → start() loops. Try/catch around start() is the
 *      belt-and-braces.
 */

// ---- Web Speech API type declarations -------------------------------------
//
// These globals aren't in the default TS DOM lib at the typescript version
// this codebase pins. Declare just the surface we use, not the full MDN
// type system — minimal blast radius if the spec evolves.
declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
  interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    start(): void;
    stop(): void;
    onresult: ((ev: SpeechRecognitionEvent) => void) | null;
    onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
  }
  interface SpeechRecognitionConstructor {
    new (): SpeechRecognition;
  }
  interface SpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: SpeechRecognitionResultList;
  }
  interface SpeechRecognitionResultList {
    readonly length: number;
    [index: number]: SpeechRecognitionResult;
  }
  interface SpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    [index: number]: SpeechRecognitionAlternative;
  }
  interface SpeechRecognitionAlternative {
    readonly transcript: string;
    readonly confidence: number;
  }
  interface SpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message: string;
  }
}

export interface MicDictationProps {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
}

const RESTART_COOLDOWN_MS = 500;

/** Delay before the auto-start check fires on mount. The description
 *  prop starts as '' before the ticket query resolves; without this
 *  delay we'd auto-start on tickets that actually have content. 500ms
 *  is below human-noticeable lag and longer than the typical API fetch. */
const AUTOSTART_SETTLE_MS = 500;

/** localStorage key for the per-device auto-start toggle. */
const AUTOSTART_LS_KEY = 'vd-mic-autostart';

/** localStorage key for the user-selected mic device id. Empty/missing
 *  means "let Chrome pick whatever it considers default". */
const DEVICE_ID_LS_KEY = 'vd-mic-device-id';

/**
 * What a recognizer error means, said to the person (17 Oct). Before, every
 * error but a denied permission was a console line while the recognizer ended
 * and was started again, so a failure that took just over the restart
 * cooldown looped for ever: the tab's microphone light flickered, the button
 * pulsed as if listening, and nothing was written (t-b4mIGp).
 */
export function dictationProblem(error: string): string {
  switch (error) {
    case 'network': return "Chrome's speech service could not be reached, so dictation stopped. Check the connection and try again.";
    case 'audio-capture': return 'No microphone could be opened. Check that one is plugged in and not in use, then try again.';
    case 'not-allowed':
    case 'service-not-allowed': return 'Microphone access is blocked for this site. Allow it in the address bar, then try again.';
    case 'language-not-supported': return "Chrome's speech recognition does not support this language.";
    default: return 'Dictation stopped unexpectedly. Try again.';
  }
}

/** Restarts in a row with nothing heard before dictation stops rather than loop. */
export const QUIET_RESTARTS_MAX = 3;

function getRecognitionCtor(): SpeechRecognitionConstructor | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition;
}

function readAutostart(): boolean {
  try {
    return localStorage.getItem(AUTOSTART_LS_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeAutostart(next: boolean): void {
  try {
    localStorage.setItem(AUTOSTART_LS_KEY, String(next));
  } catch {
    // ignore — Safari private mode etc. The state still updates in-memory.
  }
}

function readDeviceId(): string | null {
  try {
    const v = localStorage.getItem(DEVICE_ID_LS_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

function writeDeviceId(next: string | null): void {
  try {
    if (next === null) {
      localStorage.removeItem(DEVICE_ID_LS_KEY);
    } else {
      localStorage.setItem(DEVICE_ID_LS_KEY, next);
    }
  } catch {
    // ignore — Safari private mode etc.
  }
}

export function MicDictation({ textareaRef, value, onChange }: MicDictationProps) {
  const [isListening, setIsListening] = useState(false);

  // Per-device toggle: when on, auto-start dictation when the user
  // opens a ticket with an empty description. Persisted to localStorage
  // so it survives page reloads; defaults to OFF on first visit so we
  // don't surprise users with a permission-prompt-on-page-load.
  const [autostart, setAutostart] = useState<boolean>(() => readAutostart());

  // User-selected mic device id. null = "use Chrome default" (current
  // behavior). When set, MicDictation tries to claim that device via
  // getUserMedia before starting Web Speech — best-effort hint to
  // Chrome about which mic to use. The real escape hatch (per the
  // modal's footer) is the Chrome site-settings page.
  const [deviceId, setDeviceId] = useState<string | null>(() => readDeviceId());
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);

  // Mirror state into a ref for synchronous reads inside event handlers —
  // closures around `isListening` go stale between renders.
  const listeningRef = useRef(false);

  // Ref-mirrored toggle so the global Alt+M keydown listener (with empty
  // deps) always invokes the current start/stop implementations. Without
  // this, the listener would capture stale closures of start/stop.
  const toggleRef = useRef<() => void>(() => {});

  // Auto-start fire-once guard. The auto-start useEffect runs at mount
  // with a 500ms settle; this ref prevents the timeout body from
  // double-firing if React 18 strict-mode double-invokes the effect.
  const autoStartedRef = useRef(false);

  // Mirror `value` into a ref so multiple onresult events arriving in
  // quick succession all splice into the current description, not the
  // closure-captured prop value from when the handler was attached.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Persistent recognizer instance per session. Re-created on every
  // start to avoid carrying state from a botched previous run.
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  // Why dictation last stopped on its own, shown beside the button until the next start.
  const [problem, setProblem] = useState<string | null>(null);
  const quietRestartsRef = useRef(0);
  const lastRestartAtRef = useRef(0);

  // Browser-support gate: bail out before rendering anything if the API
  // isn't available. This is a render-time check (not a useEffect) so
  // unsupported browsers never see a flicker of the button.
  const hasSupport = useRef<boolean>(typeof window !== 'undefined' && !!getRecognitionCtor());

  // Defensive cleanup on unmount: if the user navigates away mid-
  // dictation, release the mic so the Chrome tab indicator goes away.
  useEffect(() => {
    return () => {
      listeningRef.current = false;
      try {
        recognitionRef.current?.stop();
      } catch {
        // ignore — stopping an already-stopped recognizer throws
      }
    };
  }, []);

  // Auto-start on mount when:
  //   - Web Speech is supported
  //   - Per-device autostart toggle is ON
  //   - Description is empty AT THE 500MS MARK (re-checked via valueRef
  //     to avoid a stale closure miss — see top of file).
  // The settle delay matters: the `value` prop starts as '' before the
  // ticket query resolves, so an immediate check would false-positive
  // on tickets that actually have content. 500ms covers the API fetch.
  // Empty-deps + autoStartedRef = fires exactly once per mount, even
  // in React 18 strict-mode double-invoke.
  useEffect(() => {
    if (!hasSupport.current) return;
    if (!autostart) return;
    if (autoStartedRef.current) return;
    const t = setTimeout(() => {
      if (autoStartedRef.current) return;
      if (valueRef.current.length > 0) return; // ticket has content; don't auto-start
      autoStartedRef.current = true;
      startRecognition();
    }, AUTOSTART_SETTLE_MS);
    return () => clearTimeout(t);
    // Intentionally empty deps: auto-start is a mount-only behavior.
    // Flipping the toggle after mount does not retroactively start
    // listening on the currently-open ticket — only future ticket loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global Alt+M shortcut to toggle dictation. Listens on `window` so it
  // works regardless of whether the textarea is focused. Requires ONLY
  // Alt — no Shift/Ctrl/Meta — to avoid colliding with browser or OS
  // shortcuts. `event.code === 'KeyM'` is layout-independent (Dvorak etc).
  useEffect(() => {
    if (!hasSupport.current) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore key-repeat: holding Alt+M would otherwise fire keydown
      // continuously, toggling start/stop tens of times per second.
      if (e.repeat) return;
      if (e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey && e.code === 'KeyM') {
        e.preventDefault();
        toggleRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!hasSupport.current) return null;

  function insertAtCursor(chunk: string) {
    const current = valueRef.current;
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? current.length;
    const end = ta?.selectionEnd ?? current.length;

    // Spacing normalization: avoid "fooBar" jam when the prior char isn't
    // whitespace and the chunk doesn't lead with one. Don't add space at
    // position 0 — that creates an awkward leading space.
    const needsLeadingSpace =
      start > 0 &&
      !/\s$/.test(current.slice(0, start)) &&
      !/^\s/.test(chunk);
    const insert = needsLeadingSpace ? ' ' + chunk : chunk;

    const next = current.slice(0, start) + insert + current.slice(end);
    valueRef.current = next; // update IMMEDIATELY for the next event
    onChange(next);

    // Restore cursor to right after the inserted chunk so subsequent
    // utterances stack naturally instead of always landing at the
    // original spot. RAF gives React a chance to commit the new value
    // before we touch the DOM selection.
    requestAnimationFrame(() => {
      if (!ta) return;
      const cursor = start + insert.length;
      try {
        ta.setSelectionRange(cursor, cursor);
      } catch {
        // some browsers throw if the element isn't focused; non-fatal
      }
    });
  }

  async function startRecognition() {
    // Idempotency guard. startRecognition can be invoked from three
    // places (manual click, Alt+M, auto-start timer); without this
    // check, a second call mid-session would construct a NEW recognizer,
    // overwrite recognitionRef.current, and leak the previous instance's
    // onresult handler — which would keep writing to the textarea
    // concurrently with the new one. Also covers Alt+M key repeat
    // landing two keydowns before React commits the isListening flip.
    if (listeningRef.current) return;

    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    setProblem(null);
    quietRestartsRef.current = 0;

    // Claim listening state BEFORE any await so concurrent calls bail
    // early via the idempotency check above. Optimistic UI flip too —
    // the button starts pulsing red while we wait for getUserMedia to
    // settle (50-200ms). Better than a dead-click feel.
    listeningRef.current = true;
    setIsListening(true);

    // Best-effort device claim. If the user has saved a mic preference,
    // acquire + immediately release a media stream from that device
    // before starting Web Speech. May hint to Chrome to use this
    // device for the upcoming recognition; the Web Speech spec has no
    // device-id param, so this is the only lever we have. Silently
    // falls back to Chrome's default if the device is unplugged,
    // permission denied, or Chromium ignores the hint.
    if (deviceId) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        });
        stream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        console.warn('[mic-dictation] device pre-claim failed, falling back to Chrome default:', err);
        // Self-clean stale deviceId. NotFoundError = device unplugged
        // or no longer available; clearing means the next dictation
        // doesn't retry the same dead device. OverconstrainedError =
        // same root cause (device disappeared between save + use).
        // Other errors (NotAllowed, etc.) keep the deviceId — user
        // can fix permissions and retry without losing their pick.
        const name = (err as { name?: string })?.name;
        if (name === 'NotFoundError' || name === 'OverconstrainedError') {
          writeDeviceId(null);
          setDeviceId(null);
        }
      }
    }

    // User toggled off (via Alt+M or stop button) during the await? Bail
    // without constructing a recognizer.
    if (!listeningRef.current) return;

    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    r.lang = 'en-US';

    r.onresult = (event) => {
      let chunk = '';
      // CRITICAL: iterate from event.resultIndex, NOT 0. Web Speech
      // hands back the full results list on every event; iterating
      // from 0 would re-insert every prior final transcript.
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        if (result.isFinal) {
          const alt = result[0];
          if (alt?.transcript) chunk += alt.transcript;
        }
      }
      // Anything heard, even an interim guess, means the recognizer is working.
      quietRestartsRef.current = 0;
      if (!chunk) return; // interim-only event; nothing to commit
      insertAtCursor(chunk);
    };

    r.onerror = (event) => {
      // 'no-speech' is the API's "I heard nothing in a while" — expected
      // on a quiet room, not user-actionable. Skip it.
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      console.error('[mic-dictation] recognition error:', event.error, event.message);
      // Every other error stops dictation and says why. It fires onend too, so
      // listening flips off BEFORE that, or onend would start it again.
      listeningRef.current = false;
      setIsListening(false);
      setProblem(dictationProblem(event.error));
    };

    r.onend = () => {
      // Continuous-listening pattern: the API auto-ends after ~5s of
      // silence. Restart if the user hasn't toggled off, but throttle
      // tight loops — Chrome throws InvalidStateError on rapid restarts.
      if (!listeningRef.current) return;
      const now = Date.now();
      if (now - lastRestartAtRef.current < RESTART_COOLDOWN_MS) {
        listeningRef.current = false;
        setIsListening(false);
        return;
      }
      lastRestartAtRef.current = now;
      // Ended again and again with nothing heard: something is wrong that no
      // error said. Stop rather than restart for ever.
      quietRestartsRef.current += 1;
      if (quietRestartsRef.current > QUIET_RESTARTS_MAX) {
        listeningRef.current = false;
        setIsListening(false);
        setProblem('Dictation kept stopping without hearing anything, so it stopped. Check the microphone and try again.');
        return;
      }
      try {
        r.start();
      } catch (err) {
        console.error('[mic-dictation] restart failed:', err);
        listeningRef.current = false;
        setIsListening(false);
      }
    };

    recognitionRef.current = r;
    lastRestartAtRef.current = Date.now();
    // (listeningRef.current + setIsListening already true from the
    // early state-claim before the device-claim await above.)

    // Move focus into the textarea so dictation inserts at a visible
    // cursor without the user having to click into the field first.
    // Clicking the mic button takes focus by default (it's a <button>);
    // this hands focus back. If the textarea was never previously
    // focused, Chrome places the caret at the end of existing text on
    // .focus() — which is the natural place to start dictating.
    textareaRef.current?.focus();

    try {
      r.start();
    } catch (err) {
      console.error('[mic-dictation] start failed:', err);
      listeningRef.current = false;
      setIsListening(false);
    }
  }

  function stopRecognition() {
    // CRITICAL: flip the ref BEFORE stop(). The stop() call triggers
    // onend, which checks listeningRef — if true at that point, it'd
    // restart in an infinite loop.
    listeningRef.current = false;
    setIsListening(false);
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore — stopping an already-stopped recognizer throws
    }
  }

  const onClick = () => {
    if (isListening) stopRecognition();
    else startRecognition();
  };

  // Refresh toggleRef every render so the global Alt+M listener picks
  // up the latest start/stop. Both closures only touch refs (no stale-
  // state risk inside them), but the function identities change each
  // render, so the ref is the safe way to keep the listener fresh
  // without re-registering the keydown handler on every render.
  toggleRef.current = onClick;

  const toggleAutostart = () => {
    const next = !autostart;
    setAutostart(next);
    writeAutostart(next);
  };

  return (
    <div className={styles.micGroup}>
      <button
        type="button"
        className={cn(styles.autoToggle, autostart && styles.autoToggleOn)}
        onClick={toggleAutostart}
        aria-pressed={autostart}
        title="Auto-start dictation when opening a new (empty) ticket"
      >
        Auto {autostart ? 'on' : 'off'}
      </button>
      <button
        type="button"
        className={cn(styles.micBtn, isListening && styles.recording)}
        onClick={onClick}
        aria-pressed={isListening}
        aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
        title={
          isListening
            ? 'Stop dictation (Alt+M)'
            : "Dictate (Alt+M) with your browser's speech recognition"
        }
      >
        <Mic size={14} />
      </button>
      <button
        type="button"
        className={styles.settingsBtn}
        onClick={() => setDevicePickerOpen(true)}
        aria-label="Microphone settings"
        title="Choose microphone"
      >
        <Settings2 size={13} />
      </button>
      {problem && <p role="alert" className={styles.problem} data-testid="mic-problem">{problem}</p>}
      <MicDevicePickerModal
        open={devicePickerOpen}
        onClose={() => setDevicePickerOpen(false)}
        selectedDeviceId={deviceId}
        onSelect={(next) => {
          setDeviceId(next);
          writeDeviceId(next);
        }}
      />
    </div>
  );
}
